<template>
    <div>
        <!-- Dialog -->
        <el-dialog
            :visible.sync="visible"
            :before-close="handleClose"
        >
            <!-- Header -->
            <template slot="title">
                <h5 class="text-dark mb-0">{{ isNewSubmission ? 'Submit Voice Integrity Bundle' : 'Voice Integrity Bundle Review 📝' }}</h5>
            </template>

            <!-- Content -->
            <el-form
                ref="voiceIntegrityForm"
                :model="voice_integrity_information"
                :rules="voice_integrity_form_rules"
                label-position="top"
            >
                <div class="row">
                    <!-- Evaluation Alerts -->
                    <div v-if="voice_integrity_evaluation && voice_integrity_evaluation.status" class="col-12 mb-2">
                        <el-alert
                            v-if="isCustomerProfileCompliant"
                            show-icon
                            type="success"
                            :closable="false"
                        >
                            <span class="text-dark">
                                Customer Profile is <b>Compliant</b>.
                            </span>
                        </el-alert>
                        <el-alert
                            v-else
                            show-icon
                            type="error"
                            class="mb-2"
                            :closable="false"
                        >
                            <span class="text-dark">
                                Customer Profile Evaluation is <b>Non-Compliant</b>.
                            </span>
                        </el-alert>
                        <el-alert
                            v-if="isVoiceIntegrityCompliant"
                            show-icon
                            type="success"
                            class="my-2"
                            :closable="false"
                        >
                            <div class="text-dark">
                                <div>Voice Integrity Bundle Evaluation is <b>Compliant</b>.</div>
                            </div>
                        </el-alert>
                        <el-alert
                            v-else
                            show-icon
                            type="error"
                            class="my-2"
                            :closable="false"
                        >
                            <span class="text-dark">
                                Voice Integrity Bundle Evaluation is <b>Non-Compliant</b>.
                                <div v-if="voiceIntegrityFailureReason" class="mt-2 failure-reason-text">
                                    <b>Reason:</b> {{ voiceIntegrityFailureReason }}
                                </div>
                            </span>
                        </el-alert>
                    </div>

                    <!-- Form Fields -->
                    <div class="col-12">
                        <!-- Customer Profile Selector (only for new submissions) -->
                        <el-form-item
                            label="Customer Profile"
                            prop="customer_profile_id"
                        >
                            <el-select
                                v-model="voice_integrity_information.customer_profile_id"
                                :disabled="!isNewSubmission"
                                class="w-full"
                                placeholder="Select customer profile"
                                filterable
                                clearable
                            >
                                <el-option
                                    class="p-0"
                                    v-for="customer_profile in customer_profiles"
                                    :key="customer_profile.id"
                                    :value="customer_profile.id"
                                    :label="`#${customer_profile.id} - ${customer_profile.sid}`"
                                    :disabled="disableCustomerProfileSelection(customer_profile)"
                                >
                                    <label>#{{ customer_profile.id }} - {{ customer_profile.sid }}</label>
                                    <small class="ml-2 text-dark font-weight-bold">
                                        <b
                                            v-if="customer_profile.status"
                                            class="label"
                                            :class="customer_profile.status | statusColor"
                                        >
                                            {{ customer_profile.status_name }}
                                        </b>
                                    </small>
                                </el-option>
                            </el-select>
                        </el-form-item>

                        <el-form-item label="Use Case" prop="use_case">
                            <el-select
                                v-model="voice_integrity_information.use_case"
                                :disabled="!isSubmittable"
                                class="w-full"
                                placeholder="Select use case"
                                filterable
                                clearable
                            >
                                <el-option
                                    v-for="use_case in voiceIntegrityUseCases"
                                    :key="use_case"
                                    :value="use_case"
                                    :label="use_case"
                                />
                            </el-select>
                        </el-form-item>

                        <div class="row">
                            <div class="col-md-6">
                                <el-form-item label="Business Employee Count" prop="business_employee_count">
                                    <el-input-number
                                        v-model="voice_integrity_information.business_employee_count"
                                        :key="`employee-count-${voice_integrity_bundle_id}`"
                                        :disabled="!isSubmittable"
                                        :min="1"
                                        style="width: 100%"
                                        placeholder="Enter number of employees"
                                    />
                                </el-form-item>
                            </div>

                            <div class="col-md-6">
                                <el-form-item label="Average Business Day Call Volume" prop="average_business_day_call_volume">
                                    <el-input-number
                                        v-model="voice_integrity_information.average_business_day_call_volume"
                                        :key="`call-volume-${voice_integrity_bundle_id}`"
                                        :disabled="!isSubmittable"
                                        :min="1"
                                        style="width: 100%"
                                        placeholder="Enter estimated calls per business day"
                                    />
                                </el-form-item>
                            </div>
                        </div>

                        <el-form-item label="Notes" prop="notes">
                            <el-input
                                v-model="voice_integrity_information.notes"
                                type="textarea"
                                :rows="4"
                                :disabled="!isSubmittable"
                                :maxlength="1000"
                                show-word-limit
                                placeholder="Optional additional notes about the use case"
                            />
                        </el-form-item>
                    </div>

                    <!-- Action Buttons -->
                    <div class="col-12">
                        <div class="row mt-3">
                            <div class="col-md-12 text-right">
                                <!-- Cancel Button -->
                                <el-button
                                    @click="handleClose"
                                >
                                    Cancel
                                </el-button>

                                <!-- Submit/Resubmit Button -->
                                <el-tooltip
                                    v-if="!isSubmittable"
                                    effect="dark"
                                    placement="top"
                                    :content="submitButtonTooltip"
                                >
                                    <el-button
                                        type="primary"
                                        :disabled="true"
                                        class="ml-2"
                                    >
                                        {{ isNewSubmission ? 'Submit' : 'Resubmit' }}
                                    </el-button>
                                </el-tooltip>
                                <el-button
                                    v-else
                                    type="primary"
                                    @click="submitOrResubmit"
                                    :loading="submit_button_loading"
                                    class="ml-2"
                                >
                                    {{ isNewSubmission ? 'Submit' : 'Resubmit' }}
                                </el-button>
                            </div>
                        </div>
                    </div>
                </div>
            </el-form>
        </el-dialog>
    </div>
</template>

<script>
import {compliance_mixin} from '../../../mixins'
import * as ComplianceBundleStatuses from '../../../constants/compliance-bundle-statuses'
import { VOICE_INTEGRITY_USE_CASES } from '../../../constants/voice-integrity-use-cases'

export default {
    name: 'VoiceIntegrityReviewDialog',
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        voice_integrity_bundle_id: {
            default: null,
            required: false,
        },
        customer_profiles: {
            type: Array,
            default: () => [],
        },
        company: {
            type: Object,
            default: null,
        },
    },
    mixins: [
        compliance_mixin,
    ],
    data() {
        return {
            ComplianceBundleStatuses,
            voiceIntegrityUseCases: VOICE_INTEGRITY_USE_CASES,
            submit_button_loading: false,
            bundle_status: null,
            failure_reason: null,
            voice_integrity_information: {
                customer_profile_id: null,
                use_case: null,
                business_employee_count: null,
                average_business_day_call_volume: null,
                notes: null,
            },
            voice_integrity_evaluation: null,
        }
    },
    created() {
        if (this.voice_integrity_bundle_id) {
            this.getVoiceIntegrityInformation()
        }
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },
        getVoiceIntegrityInformation() {
            // Prevent request if no voice_integrity_bundle_id
            if (!this.voice_integrity_bundle_id) {
                return
            }

            axios
                .get(`/api/admin/compliance/voice-integrity-bundles/${this.voice_integrity_bundle_id}/information`)
                .then((res) => {
                    // Store bundle status
                    this.bundle_status = res.data.status

                    // Store failure reason if it exists
                    this.failure_reason = res.data.failure_reason || null

                    // Store evaluation if it exists
                    this.voice_integrity_evaluation = res.data.voice_integrity_evaluation || null

                    // Extract voice integrity information
                    // Ensure numeric values are properly converted to numbers for el-input-number
                    this.voice_integrity_information = {
                        customer_profile_id: res.data.customer_profile_id || null,
                        use_case: res.data.use_case || null,
                        business_employee_count: res.data.business_employee_count != null ? Number(res.data.business_employee_count) : 1,
                        average_business_day_call_volume: res.data.average_business_day_call_volume != null ? Number(res.data.average_business_day_call_volume) : 0,
                        notes: res.data.notes || null,
                    }
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response)
                })
        },
        disableCustomerProfileSelection(customer_profile) {
            return customer_profile.status === ComplianceBundleStatuses.STATUS_STAGED
        },
        submitOrResubmit() {
            this.$refs.voiceIntegrityForm.validate((valid) => {
                if (!valid) {
                    return false
                }

                this.submit_button_loading = true

                if (this.isNewSubmission) {
                    // New submission
                    axios
                        .post(
                            `/api/admin/compliance/${this.company.id}/voice-integrity-bundles/${this.voice_integrity_information.customer_profile_id}`,
                            {
                                use_case: this.voice_integrity_information.use_case,
                                business_employee_count: this.voice_integrity_information.business_employee_count,
                                average_business_day_call_volume: this.voice_integrity_information.average_business_day_call_volume,
                                notes: this.voice_integrity_information.notes || null,
                            }
                        )
                        .then(() => {
                            this.submit_button_loading = false
                            this.$notify({
                                title: "Voice Integrity Bundle Registration",
                                message: "Operation is being processed...",
                                type: "success",
                                showClose: true,
                            })
                            this.handleClose()
                            this.$emit('updated')
                        })
                        .catch((error) => {
                            this.submit_button_loading = false
                            this.$root.handleErrors(error.response)
                        })
                } else {
                    // Resubmission
                    axios
                        .post(`/api/admin/compliance/voice-integrity-bundles/${this.voice_integrity_bundle_id}/resubmit`, {
                            use_case: this.voice_integrity_information.use_case,
                            business_employee_count: this.voice_integrity_information.business_employee_count,
                            average_business_day_call_volume: this.voice_integrity_information.average_business_day_call_volume,
                            notes: this.voice_integrity_information.notes || null,
                        })
                        .then((res) => {
                            this.submit_button_loading = false
                            this.$message.success('Voice Integrity Bundle resubmitted successfully.')
                            this.handleClose()
                            this.$emit('updated')
                        })
                        .catch((error) => {
                            this.submit_button_loading = false
                            let message = 'Request failed'
                            if (error.response && error.response.data && error.response.data.message) {
                                message = error.response.data.message
                            } else if (error.response && error.response.statusText) {
                                message = error.response.statusText
                            } else if (error.message) {
                                message = error.message
                            }

                            this.$message({
                                message: message,
                                type: 'error',
                            })
                        })
                }
            })
        },
    },
    computed: {
        voice_integrity_form_rules() {
            const rules = {
                use_case: [
                    { required: true, message: 'Please select a use case', trigger: 'change' },
                ],
                business_employee_count: [
                    { required: true, message: 'Please enter business employee count', trigger: 'blur' },
                    { type: 'number', min: 1, message: 'Business employee count must be at least 1', trigger: 'blur' },
                ],
                average_business_day_call_volume: [
                    { required: true, message: 'Please enter average business day call volume', trigger: 'blur' },
                    { type: 'number', min: 1, message: 'Average business day call volume must be at least 1', trigger: 'blur' },
                ],
                notes: [
                    { max: 1000, message: 'Notes may not be greater than 1000 characters', trigger: 'blur' },
                ],
            }

            // Only require customer_profile_id for new submissions
            if (this.isNewSubmission) {
                rules.customer_profile_id = [
                    { required: true, message: 'Please select a customer profile', trigger: 'change' },
                ]
            }

            return rules
        },
        isCustomerProfileCompliant() {
            if (!this.voice_integrity_evaluation?.status) {
                return false
            }

            // Results is an object keyed by requirement_name
            const results = this.voice_integrity_evaluation.results || {}
            const customerProfileReq = results['customer_profile']
            return customerProfileReq?.passed === true
        },
        isVoiceIntegrityCompliant() {
            if (!this.voice_integrity_evaluation?.status) {
                const results = this.voice_integrity_evaluation.results || {}
                const voiceIntegrityReq = results['voice_integrity_information']

                return voiceIntegrityReq.passed === true
            }

            return !this.failure_reason
        },
        voiceIntegrityFailureReason() {
            return this.failure_reason
        },
        isNewSubmission() {
            return !this.voice_integrity_bundle_id
        },
        isSubmittable() {
            // For new submissions, always submittable if form is valid
            if (this.isNewSubmission) {
                return true
            }

            // For existing bundles, submittable when status is REJECTED
            return this.bundle_status === ComplianceBundleStatuses.STATUS_REJECTED
        },
        submitButtonTooltip() {
            if (this.isNewSubmission) {
                return 'Please fill in all required fields to submit.'
            }

            if (this.bundle_status === ComplianceBundleStatuses.STATUS_REJECTED) {
                return 'Bundle is rejected. Please fix the issues and resubmit.'
            }

            return 'Submission is not available for this status.'
        },
    },
    watch: {
        voice_integrity_bundle_id(newVal) {
            if (newVal) {
                this.getVoiceIntegrityInformation()
            } else {
                // Reset form for new submission
                this.voice_integrity_information = {
                    customer_profile_id: null,
                    use_case: null,
                    business_employee_count: null,
                    average_business_day_call_volume: null,
                    notes: null,
                }
                this.bundle_status = null
                this.failure_reason = null
                this.voice_integrity_evaluation = null
            }

            // Clear validation when bundle ID changes (including when switching between bundles)
            this.$nextTick(() => {
                if (this.$refs.voiceIntegrityForm) {
                    this.$refs.voiceIntegrityForm.clearValidate()
                }
            })
        },
        visible(newVal) {
            if (newVal) {
                // Clear validation when dialog opens
                this.$nextTick(() => {
                    if (this.$refs.voiceIntegrityForm) {
                        this.$refs.voiceIntegrityForm.clearValidate()
                    }
                })

                if (!this.voice_integrity_bundle_id) {
                    // Reset form when opening for new submission
                    this.voice_integrity_information = {
                        customer_profile_id: null,
                        use_case: null,
                        business_employee_count: null,
                        average_business_day_call_volume: null,
                        notes: null,
                    }
                    this.bundle_status = null
                    this.failure_reason = null
                    this.voice_integrity_evaluation = null
                }
            }
        }
    },
};
</script>

<style scoped>
.failure-reason-text {
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: none;
}
</style>
